import { ChevronDownIcon } from '@chakra-ui/icons';
import { Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { useCallback } from 'react';

import {
  PreferenceScope,
  UserPreferenceKey,
  useUserPreferences,
} from '../../../../../shared/user-preferences';
import { PreferenceBrowserStorage } from '../../../../../shared/user-preferences/utils/preference-session-storage';
import { useTabContentConfig } from '../../../components/PageConfigProvider/PageConfigProvider';
import type { GeographyMode } from '../../hooks/useGeographyMode';
import { useSupportsStateGeography } from '../../hooks/useSupportsStateGeography';

const OPTIONS: { value: GeographyMode; label: string }[] = [
  { value: 'msa', label: 'Metro Area' },
  { value: 'administrative', label: 'State' },
];

/**
 * Renders an inline dropdown that replaces the branch label in the 3rd popout
 * panel of the geography tree, allowing users to switch between State and
 * Metro Area granularity directly from the tree.
 */
export const GeographyModeBranchLabel = () => {
  const { revelioPageId } = useTabContentConfig();
  const supportsStateGeography = useSupportsStateGeography(revelioPageId);
  const { preferences } = useUserPreferences(PreferenceScope.GLOBAL);

  const currentMode: GeographyMode =
    preferences[UserPreferenceKey.Geography] === 'administrative'
      ? 'administrative'
      : 'msa';

  const currentLabel =
    OPTIONS.find((o) => o.value === currentMode)?.label ?? 'Metro Area';

  const onSelect = useCallback((mode: GeographyMode) => {
    PreferenceBrowserStorage.update(
      PreferenceScope.GLOBAL,
      UserPreferenceKey.Geography,
      mode
    );
  }, []);

  if (!supportsStateGeography) {
    // Show static "Metro Area" label when state is not supported on this page
    return (
      <Text
        fontSize="var(--chakra-fontSizes-xs)"
        color="#96a0b5"
        fontWeight={600}
        letterSpacing="0.8px"
      >
        Metro Area
      </Text>
    );
  }

  return (
    <Menu placement="bottom-start" gutter={2}>
      <MenuButton
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '2px',
          marginBottom: '1px',
          fontSize: 'var(--chakra-fontSizes-xs)',
          color: '#96a0b5',
          fontWeight: 600,
          letterSpacing: '0.8px',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          _hover: { color: '#6b7590' },
        }}
      >
        {currentLabel}
        <ChevronDownIcon boxSize={3} />
      </MenuButton>
      <MenuList minW="120px" fontSize="xs" zIndex={10} boxShadow="lg" py={1}>
        {OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => onSelect(option.value)}
            fontWeight={option.value === currentMode ? 600 : 400}
            color={option.value === currentMode ? '#96a0b5' : 'gray.600'}
            fontSize="xs"
          >
            <Text>{option.label}</Text>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
